// z58 (Diego, 28-oct-2026): «al hacer una de sin consumos, ya sea aislado o híbrido, me gustaría que sea más sencillo: un
// botón o sección que diga CALCULAR CONSUMOS CON IA, y después poder modificarlos o ajustarlos y ya AUTORIZAR eso para
// continuar haciendo la propuesta». Aquí vive la cuenta de la tabla de 12 meses que el vendedor ajusta: lo que enseña la
// pantalla es EXACTAMENTE lo que va a cotizar el motor con lo que se le manda (cuestionario.estimacion, sin campos nuevos).
//
// Cómo reparte el motor (paquetes/motor/src/periodos.ts → periodosNuevos):
//  · casa, negocio o industria nuevos (`consumo_estacional`): el cuadro de cargas con la curva de Sonora → lo de todo el
//    año va parejo (la «base») y lo de temporada (los aires) sigue la curva medida. Dos perillas: base y temporada.
//  · baterías (híbrido) y aislado: ahí `consumo_estacional` no aplica y el cuadro saldría en DOS BLOQUES; con «kWh al mes»
//    el motor reparte el promedio con la curva de casas de Sonora. Por eso esos dos se mandan como `kwh_mes` (el promedio
//    de la tabla): la curva que ve el vendedor es la que cotiza el motor. Una perilla: el promedio.
import { DIAS_MES_CUADRO_CARGAS, PERFIL_CONSUMO_CASA, consumoEstacionalAplica, consumoPorMesDeCargas, kwhMesDeCarga } from '@cotizador/compartido';
import type { TipoProyecto } from '@cotizador/compartido';
import type { FormaCarga } from './cuestionario.ts';

export const MESES_TABLA = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] as const;
const SUMA_CURVA = PERFIL_CONSUMO_CASA.reduce((a, b) => a + b, 0);
/** Nombre del renglón que se agrega cuando el vendedor sube una parte que la IA dejó en cero. */
export const EQUIPO_AJUSTE_BASE = 'Ajuste del vendedor (todo el año)';
export const EQUIPO_AJUSTE_TEMPORADA = 'Ajuste del vendedor (temporada de calor)';

const num = (s: string) => {
  const x = Number(String(s).replace(/,/g, '').trim());
  return Number.isFinite(x) && x >= 0 ? x : 0;
};
const r2 = (x: number) => Math.round(x * 100) / 100;

/** ¿El motor reparte el cuadro de cargas de este tipo con la curva (consumo_estacional)? */
export function cuadroConCurva(tipo: TipoProyecto | null): boolean {
  return tipo !== null && consumoEstacionalAplica({ tipo, consumo: { es_nuevo: true } });
}

/** Los 12 meses (ENE…DIC) que va a cotizar el motor con este cuadro y este tipo. */
export function mesesDeCargas(tipo: TipoProyecto | null, cargas: readonly FormaCarga[]): number[] {
  const n = cargas.map((c) => ({ cantidad: num(c.cantidad), w: num(c.w), horas_dia: num(c.horas_dia), meses_uso: num(c.meses_uso) }));
  if (cuadroConCurva(tipo)) return consumoPorMesDeCargas(n, { estacional: true });
  const anual = consumoPorMesDeCargas(n).reduce((a, b) => a + b, 0);
  return PERFIL_CONSUMO_CASA.map((p) => (anual * p) / SUMA_CURVA);
}

/** Promedio al mes que se manda como `kwh_mes` (baterías y aislado), con dos decimales. */
export function promedioDeCargas(tipo: TipoProyecto | null, cargas: readonly FormaCarga[]): number {
  return r2(mesesDeCargas(tipo, cargas).reduce((a, b) => a + b, 0) / 12);
}

const escalar = (c: FormaCarga, f: number): FormaCarga => ({ ...c, w: String(Math.max(0.01, r2(num(c.w) * f))) });
const esBase = (c: FormaCarga) => num(c.meses_uso) >= 12;

/** Cambia el promedio: todos los equipos se escalan igual (la curva se conserva). */
export function ajustarPromedio(tipo: TipoProyecto | null, cargas: readonly FormaCarga[], promedio: number): FormaCarga[] {
  const anual = mesesDeCargas(tipo, cargas).reduce((a, b) => a + b, 0);
  if (!(anual > 0) || !(promedio > 0)) return [...cargas];
  return cargas.map((c) => escalar(c, (promedio * 12) / anual));
}

/** Renglón de ajuste con los kWh al mes dados (1 equipo, 24 h; `meses` = 12 para la base, 6 para la temporada). */
function renglonAjuste(equipo: string, kwhMes: number, meses: number): FormaCarga {
  return { equipo, cantidad: '1', w: String(r2((kwhMes * 1000) / (DIAS_MES_CUADRO_CARGAS * 24))), horas_dia: '24', meses_uso: String(meses) };
}

/** Cambia UN mes. Con curva (casa/negocio): en un mes de calor se mueve la temporada (los aires) y la base se queda; en un
 *  mes frío se mueve la base y la temporada se queda. Los demás meses se acomodan con la curva. Sin curva propia (baterías,
 *  aislado): todo se escala para que ese mes quede como lo escribió. */
export function ajustarMes(tipo: TipoProyecto | null, cargas: readonly FormaCarga[], mes: number, valor: number): FormaCarga[] {
  if (!(valor > 0)) return [...cargas];
  const meses = mesesDeCargas(tipo, cargas);
  if (!cuadroConCurva(tipo)) return meses[mes]! > 0 ? cargas.map((c) => escalar(c, valor / meses[mes]!)) : [...cargas];
  const p = PERFIL_CONSUMO_CASA[mes]! / SUMA_CURVA;
  const base = cargas.filter(esBase).reduce((a, c) => a + kwhMesDeCarga({ cantidad: num(c.cantidad), w: num(c.w), horas_dia: num(c.horas_dia) }), 0);
  const temporada = cargas.filter((c) => !esBase(c)).reduce((a, c) => a + kwhMesDeCarga({ cantidad: num(c.cantidad), w: num(c.w), horas_dia: num(c.horas_dia) }) * Math.round(num(c.meses_uso)), 0);
  let b = base;
  let t = temporada;
  if (p > 1 / 12) {
    if (valor >= base) t = (valor - base) / p;
    else [b, t] = [valor, 0];
  } else {
    b = valor - temporada * p;
    if (b < 0) [b, t] = [0, valor / p];
  }
  const fuera = (c: FormaCarga) => (esBase(c) ? b <= 0 : t <= 0);
  const out = cargas.filter((c) => !fuera(c)).map((c) => (esBase(c) ? (base > 0 ? escalar(c, b / base) : c) : temporada > 0 ? escalar(c, t / temporada) : c));
  if (base <= 0 && b > 0) out.push(renglonAjuste(EQUIPO_AJUSTE_BASE, b, 12));
  if (temporada <= 0 && t > 0) out.push(renglonAjuste(EQUIPO_AJUSTE_TEMPORADA, t / 6, 6));
  return out;
}

/** Huella de lo que se autoriza (cuántos, watts, horas y meses de cada renglón, y el tipo que decide el reparto). Si cambia
 *  algo después de «Usar estos consumos», la autorización ya no vale y la pantalla lo dice. */
export function firmaConsumos(tipo: TipoProyecto | null, cargas: readonly FormaCarga[]): string {
  return JSON.stringify([tipo, cargas.map((c) => [num(c.cantidad), num(c.w), num(c.horas_dia), num(c.meses_uso)])]);
}
